import { useState, useCallback } from 'react';
import { PincodeDetails, PincodeTargetingPayload } from '../types/pincodeTypes';

export interface PincodeSelectionState {
  isPincodeSpecific: boolean;
  targetPincodes: PincodeDetails[];
  targetPincodeIds: number[];
}

export interface PincodeSelectionActions {
  togglePincodeSpecific: (enabled?: boolean) => void;
  addPincode: (pincode: PincodeDetails) => void;
  removePincode: (pincodeId: number) => void;
  setPincodes: (pincodes: PincodeDetails[]) => void;
  clearSelection: () => void;
  getPayload: () => PincodeTargetingPayload;
  validateSelection: () => boolean;
}

export interface PincodeSelection extends PincodeSelectionState, PincodeSelectionActions {}

export const usePincodeSelection = (initialState?: Partial<PincodeSelectionState>): PincodeSelection => {
  const [state, setState] = useState<PincodeSelectionState>({
    isPincodeSpecific: false,
    targetPincodes: [],
    targetPincodeIds: [],
    ...initialState
  });

  const togglePincodeSpecific = useCallback((enabled?: boolean) => {
    setState(prevState => {
      const newEnabled = enabled !== undefined ? enabled : !prevState.isPincodeSpecific;
      return {
        ...prevState,
        isPincodeSpecific: newEnabled,
        // Clear pincodes when disabling
        targetPincodes: newEnabled ? prevState.targetPincodes : [],
        targetPincodeIds: newEnabled ? prevState.targetPincodeIds : []
      };
    });
  }, []);

  const addPincode = useCallback((pincode: PincodeDetails) => {
    setState(prevState => {
      // Check if pincode already exists
      const exists = prevState.targetPincodes.some(p => p.pincodeId === pincode.pincodeId);
      if (exists) {
        return prevState;
      }

      const newPincodes = [...prevState.targetPincodes, pincode];
      return {
        ...prevState,
        targetPincodes: newPincodes,
        targetPincodeIds: newPincodes.map(p => p.pincodeId)
      };
    });
  }, []);

  const removePincode = useCallback((pincodeId: number) => {
    setState(prevState => {
      const newPincodes = prevState.targetPincodes.filter(p => p.pincodeId !== pincodeId);
      return {
        ...prevState,
        targetPincodes: newPincodes,
        targetPincodeIds: newPincodes.map(p => p.pincodeId)
      };
    });
  }, []);

  const setPincodes = useCallback((pincodes: PincodeDetails[]) => {
    setState(prevState => ({
      ...prevState,
      targetPincodes: pincodes,
      targetPincodeIds: pincodes.map(p => p.pincodeId)
    }));
  }, []);

  const clearSelection = useCallback(() => {
    setState({
      isPincodeSpecific: false,
      targetPincodes: [],
      targetPincodeIds: []
    });
  }, []);

  const getPayload = useCallback((): PincodeTargetingPayload => {
    return {
      isPincodeSpecific: state.isPincodeSpecific,
      targetPincodes: state.targetPincodes,
      targetPincodeIds: state.targetPincodeIds
    };
  }, [state]);

  const validateSelection = useCallback((): boolean => {
    if (!state.isPincodeSpecific) {
      return true; // Not using pincode targeting, so it's valid
    }
    
    return state.targetPincodes.length > 0; // Must have at least one pincode selected
  }, [state]);

  return {
    ...state,
    togglePincodeSpecific,
    addPincode,
    removePincode,
    setPincodes,
    clearSelection,
    getPayload,
    validateSelection
  };
};
